/**
 * AudioEngine — tiny synthesized WebAudio engine for Poparium.
 *
 * Zero libraries, zero samples, zero network bytes. Everything audible is
 * generated at call time from oscillators/noise buffers. This module owns
 * the shared AudioContext, the master signal chain (compressor -> master
 * gain -> destination), mute persistence, and a small concurrent-voice cap
 * so a burst of pops never turns into a wall of clipped noise.
 */

const MUTE_STORAGE_KEY = "poparium.muted";
const MAX_VOICES = 12;
const MASTER_GAIN_VALUE = 0.9;
const MUTE_RAMP_SECONDS = 0.03;

let audioContext: AudioContext | null = null;
let compressorNode: DynamicsCompressorNode | null = null;
let masterGainNode: GainNode | null = null;

let muted = false;
let activeVoiceCount = 0;
let webAudioUnavailable = false;

function readStoredMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStoredMuted(value: boolean): void {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? "true" : "false");
  } catch {
    // Ignore (private browsing / storage disabled).
  }
}

// Initialize mute state from storage as soon as the module loads. This is
// synchronous, storage-only work and does not touch the AudioContext, so it
// is safe to do outside of a user gesture.
muted = readStoredMuted();

/**
 * Lazily creates the AudioContext (and the master signal chain) on first
 * call, and resumes it. Safe to call repeatedly, including from every
 * pointerdown/keydown — cheap no-op once the engine is up. Never throws:
 * if Web Audio is unavailable or construction fails for any reason, the
 * engine simply stays silent forever.
 */
export function ensureAudio(): void {
  if (webAudioUnavailable) {
    return;
  }

  try {
    if (!audioContext) {
      const AudioContextCtor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;

      if (!AudioContextCtor) {
        webAudioUnavailable = true;
        return;
      }

      const context = new AudioContextCtor();

      const compressor = context.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-18, context.currentTime);
      compressor.ratio.setValueAtTime(6, context.currentTime);
      compressor.attack.setValueAtTime(0.003, context.currentTime);
      compressor.release.setValueAtTime(0.12, context.currentTime);
      compressor.knee.setValueAtTime(6, context.currentTime);

      const masterGain = context.createGain();
      masterGain.gain.setValueAtTime(
        muted ? 0 : MASTER_GAIN_VALUE,
        context.currentTime,
      );

      compressor.connect(masterGain);
      masterGain.connect(context.destination);

      audioContext = context;
      compressorNode = compressor;
      masterGainNode = masterGain;
    }

    if (audioContext.state === "suspended") {
      void audioContext.resume().catch(() => {
        // Ignore — a future gesture-triggered call will retry.
      });
    }
  } catch {
    webAudioUnavailable = true;
    audioContext = null;
    compressorNode = null;
    masterGainNode = null;
  }
}

/**
 * Mutes/unmutes the engine, ramped smoothly (~30ms) to avoid clicks, and
 * persists the choice to localStorage. Safe to call before the audio
 * context exists — the value is simply remembered for when it's created.
 */
export function setMuted(nextMuted: boolean): void {
  muted = nextMuted;
  writeStoredMuted(nextMuted);

  if (!audioContext || !masterGainNode) {
    return;
  }

  try {
    const now = audioContext.currentTime;
    const target = nextMuted ? 0 : MASTER_GAIN_VALUE;
    masterGainNode.gain.cancelScheduledValues(now);
    masterGainNode.gain.setValueAtTime(masterGainNode.gain.value, now);
    masterGainNode.gain.linearRampToValueAtTime(
      target,
      now + MUTE_RAMP_SECONDS,
    );
  } catch {
    // If ramping fails for any reason, leave state as-is; nothing audible
    // is guaranteed to be playing at this instant.
  }
}

/** Returns the current mute state (default unmuted). */
export function isMuted(): boolean {
  return muted;
}

/** Returns the shared AudioContext, or null if audio is unavailable. */
export function getContext(): AudioContext | null {
  return audioContext;
}

/**
 * Returns the master input node that all voices should connect into:
 * (voices) -> DynamicsCompressorNode -> GainNode(master) -> destination.
 * Returns null if audio is unavailable.
 */
export function getMaster(): AudioNode | null {
  return compressorNode;
}

/**
 * Reserves a concurrent voice slot (cap MAX_VOICES). Returns false if the
 * engine is already at capacity — callers should skip playing entirely.
 */
export function claimVoice(): boolean {
  if (activeVoiceCount >= MAX_VOICES) {
    return false;
  }
  activeVoiceCount += 1;
  return true;
}

/** Releases a previously claimed voice slot. Never goes below zero. */
export function releaseVoice(): void {
  activeVoiceCount = Math.max(0, activeVoiceCount - 1);
}
